import React, { useState, useRef, useEffect } from "react";
import { Heading } from "../../../styles/component/style";
import styled, { keyframes } from "styled-components";
import theme from "../../../assets/theme";
import {
  ExpansionPanel,
  ExpansionPanelSummary,
  ExpansionPanelDetails,
  makeStyles,
} from "@material-ui/core";
import moment from "moment";
import useLoader from "../../../hooks/useLoader";
import AWSService from "../../../services/AWSService";
import { useSnackbar } from "notistack";
import {
  getErrorMessage,
  parseTimeStamp,
  numberFormat,
} from "../../../helpers/functions";
import CaseService from "../../../services/CaseService";
import CaseDescriptionModal from "../CaseDescriptionModal";
import AgentListModal from "../AgentListModal/AgentListDrawer";
import DocumentUploadModal from "../MultipleDocumentUploadModal";
import RefundModal from "../RefundModal";
import Accordion from "@material-ui/core/ExpansionPanel";
import AccordionSummary from "@material-ui/core/ExpansionPanelSummary";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ViewNotesModal from "../../internal/ViewNotesModal";
import ClaimItemsListModal from "../../common/ClaimItemsListModal/index";
import HearingDate from "../../internal/HearingDate/index";
import AlertDialog from "../Alert";
import CounterClaimModal from "../ClaimItemsListModal/CounterClaim";
import GenerateInvoiceModal from "../ClaimItemsListModal/GenerateInvoice";
import HearingTimeline from "../HearingTimeLine";
import { HyperLink } from "../AgentListModal/RespondentDrawer";
import TrackingModal from "./component/TrackingModal";
import RespondentPartiesModal from "./component/RespondentPartiesModal";
import NumberFormat from "react-number-format";
import { CustomInputField } from "../FormInputs";
import { CloseBtn } from "../../internal/BulkUpdate";
import DisputeModal from "./component/DisputeModal";

const useStyles = makeStyles((theme) => ({
  root: {
    boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.08)",
    border: "none",
    borderRadius: "8px",
    background: "linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)",
  },
  content: {
    margin: `0 !important`,
    "& .MuiExpansionPanelSummary-content.Mui-expanded": {
      margin: `0 !important`,
    },
  },
  details: {
    display: "flex",
    flexDirection: "column",
    padding: `0 !important`,
  },
}));

const ClaimValueModal = ({ open, onClose, caseDetails, onSuccess }) => {
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();
  const [value, setValue] = useState(caseDetails?.totalClaimValue || "");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setValue(caseDetails?.totalClaimValue || "");
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const handleSave = async () => {
    if (!value && value !== 0) {
      setError("Claim value is required");
      return;
    }
    try {
      setSaving(true);
      setLoader({ state: true, message: "Updating claim value..." });
      await CaseService.updateCase(caseDetails.id, { totalClaimValue: value });
      enqueueSnackbar("Claim value updated successfully", {
        variant: "success",
      });
      onSuccess();
      onClose();
    } catch (err) {
      const message = getErrorMessage(err);
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setSaving(false);
      setLoader({ state: false });
    }
  };

  return (
    <ModalOverlay onClick={(e) => e.target === e.currentTarget && onClose()}>
      <ModalBox>
        <ModalHeader>
          <ModalTitleGroup>
            <div>
              <ModalTitle>Update Claim Value</ModalTitle>
              <ModalSub>Enter the updated total claim amount</ModalSub>
            </div>
          </ModalTitleGroup>
          <CloseBtn onClick={onClose} aria-label="Close">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </CloseBtn>
        </ModalHeader>

        <ModalBody>
          <FieldLabel>
            Claim Value <span className="required-star"> *</span>
          </FieldLabel>
          <NumberFormat
            thousandSeparator
            thousandsGroupStyle="lakh"
            isNumericString={true}
            decimalScale={2}
            fixedDecimalScale
            allowNegative={false}
            className={"input-white"}
            displayType={"input"}
            value={value}
            type={"text"}
            customInput={CustomInputField}
            name={"totalClaimValue"}
            onValueChange={({ value: v }) => {
              setValue(v);
              if (v) setError("");
            }}
            variant="outlined"
            helperText={error}
            error={!!error}
            placeholder="0.00"
          />
          {caseDetails?.currencyUnit && (
            <CurrencyHint>Currency: {caseDetails.currencyUnit}</CurrencyHint>
          )}
        </ModalBody>

        <ModalFooter>
          <SaveBtn onClick={handleSave} disabled={saving}>
            {saving ? (
              <SpinnerWrap>
                <Spinner />
                Saving...
              </SpinnerWrap>
            ) : (
              "Update Claim Value"
            )}
          </SaveBtn>
        </ModalFooter>
      </ModalBox>
    </ModalOverlay>
  );
};

const Index = ({
  caseDetails,
  setState,
  id,
  MTRef,
  isActionsDisabled = false,
  partyList = [],
  isViewAction = false,
  pendingRegistrationFee,
  setOpenPaymentBox,
  disableRefund,
  refreshDetails,
  setRefreshDetails,
  docUploadModal,
  setDocUploadModal,
  fields,
  setFields,
  viewOnly,
  requestedSmartOdrAction,
  setRequestedSmartOdrAction,
  onSmartOdrActionContinue,
}) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(true);
  const fileRef = useRef();
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();
  const [file] = useState([]);
  const [modal, setModal] = useState();
  const [url, setUrl] = useState("");
  const [openRefundModal, setOpenRefundModal] = useState(false);
  const [opendrawer, setOpenDrawer] = useState(false);
  const [viewNotes, setViewNotes] = useState(false);
  const [opendrawerClaim, setOpenDrawerClaim] = useState(false);
  const [openHearing, setOpenHearing] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [alertData, setAlertData] = useState({});
  const [data, setData] = useState([]);
  const [claimModal, setClaimModal] = useState(false);
  const [invoiceModal, setInvoiceModal] = useState({ state: false });
  const [timelineModal, setTimelineModal] = useState({ state: false });
  const [trackingModal, setTrackingModal] = useState({ state: false });
  const [respondentModal, setRespondentModal] = useState(false);
  const [claimValueModal, setClaimValueModal] = useState(false);
  const [disputeModal, setDisputeModal] = useState({});

  async function s3Upload(file) {
    if (file) {
      let len = file.length;
      let fileName = [];
      for (let i = 0; i < len; i++) fileName.push(file[i]?.name);
      try {
        setLoader({ state: true, message: `Uploading file...` });
        const payload = { key: fileName };
        const s3Response = await AWSService.getS3URL(payload);
        if (s3Response.url) {
          const s3url = [];
          for (let i = 0; i < len; i++) {
            const res = await AWSService.uploadToS3(
              s3Response?.url[i],
              file[i],
            );
            s3url.push(res);
          }
          setUrl(s3url);
        }
      } catch (error) {
        const message = getErrorMessage(error);
        enqueueSnackbar(message, { variant: "error" });
      } finally {
        setLoader({ state: false });
      }
    }
  }

  async function addDocumentForCase(data) {
    try {
      setLoader({ state: true, message: `Uploading document` });
      const documentDetails = data;
      await CaseService.caseAddDocument({ documentDetails }, id);
      enqueueSnackbar("Successfully Uploaded", { variant: "success" });
      if (MTRef?.current?.onSearchChange) MTRef.current.onSearchChange("");
      setData([]);
    } catch (error) {
      const message = getErrorMessage(error);
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setLoader({ state: false });
      setOpenAlert(false);
      setDocUploadModal(false);
      setAlertData({});
    }
  }

  async function postReportDetails() {
    try {
      setLoader({ state: true, message: `posting` });
      const res = await CaseService.postReportDetails(
        id,
        caseDetails.resolutionKind,
      );
      if (res.success) {
        enqueueSnackbar(res?.message, { variant: "success" });
      }
    } catch (error) {
      const message = getErrorMessage(error);
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setLoader({ state: false });
    }
  }

  async function fetchReportDetails(action) {
    try {
      setLoader({ state: true, message: `fetch report...` });
      const res = await CaseService.fetchReportDetails(
        id,
        caseDetails.resolutionKind,
      );
      if (res.success) {
        setDisputeModal({
          state: true,
          disputeData: res?.data,
          pendingAction: action,
        });
      }
    } catch (error) {
      const message = getErrorMessage(error);
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setLoader({ state: false });
    }
  }

  const respondentDisplay = () => {
    if (caseDetails?.respondentParty?.name)
      return caseDetails?.respondentParty?.name;
    if (caseDetails.respondentName) return caseDetails.respondentName;
    if (caseDetails?.respondentParties?.length)
      return caseDetails?.respondentParties[0]?.name;
    return "";
  };

  const respondentCount = partyList?.filter(
    (el) => el.partyRole === "respondent",
  )?.length;

  useEffect(() => {
    if (!requestedSmartOdrAction) return;

    fetchReportDetails(requestedSmartOdrAction);
    setRequestedSmartOdrAction(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedSmartOdrAction]);

  const openTrackingModal = () => {
    setTrackingModal({
      state: true,
      trackingData: caseDetails?.trackingNumbers,
    });
  };

  return (
    <>
      <ExpansionPanel className={classes.root} expanded={expanded}>
        <ExpansionPanelSummary
          aria-controls="panel1a-content"
          id="panel1a-header"
          classes={{ content: classes.content }}
        >
          <HeaderWrapper>
            <CaseTitleSection>
              <CaseTitle>{caseDetails.title}</CaseTitle>
            </CaseTitleSection>
            <RightActions>
              <ToggleButton
                onClick={() => setExpanded((e) => !e)}
                expanded={expanded}
                title={expanded ? "Collapse" : "Expand"}
              >
                {expanded ? "▲" : "▼"}
              </ToggleButton>
            </RightActions>
          </HeaderWrapper>
        </ExpansionPanelSummary>

        <ExpansionPanelDetails className={classes.details}>
          <InfoGridContainer>
            <GridRow>
              <InfoCard>
                <InfoLabel>Case ID</InfoLabel>
                <InfoValue>{caseDetails.id}</InfoValue>
              </InfoCard>

              {caseDetails?.resolutionKind === "arbitration" && (
                <InfoCard>
                  <InfoLabel>Contract Number</InfoLabel>
                  <InfoValue>
                    {caseDetails.loanAccountNo
                      ? caseDetails?.loanAccountNo
                      : "-"}
                  </InfoValue>
                </InfoCard>
              )}

              <InfoCard>
                <InfoLabel>Claimant</InfoLabel>
                <InfoValue>{caseDetails?.claimantParty?.name}</InfoValue>
              </InfoCard>

              <InfoCard>
                <InfoLabel>Respondent</InfoLabel>
                <InfoValue>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    {respondentDisplay()}
                    {caseDetails?.status !== "draft" &&
                      caseDetails?.status !== "pendingRegistrationFee" &&
                      caseDetails?.status !== "proformaGenerated" &&
                      caseDetails?.status !== "waitingForCaseApproval" &&
                      caseDetails?.resolutionKind === "arbitration" && (
                        <UpdateLink
                          style={{ marginTop: 3 }}
                          onClick={() => setRespondentModal(true)}
                        >
                          + Add
                        </UpdateLink>
                      )}
                  </div>
                  {respondentCount - 1 > 0 && (
                    <MorePartiesLink
                      onClick={() =>
                        setOpenDrawer((a) => ({ ...a, state: true }))
                      }
                    >
                      +{respondentCount - 1} more
                    </MorePartiesLink>
                  )}
                </InfoValue>
              </InfoCard>
            </GridRow>

            <GridRow>
              <InfoCard>
                <InfoLabel>Claim Value</InfoLabel>
                <InfoValue>
                  <ClaimValueRow>
                    <span>
                      {caseDetails.totalClaimValue
                        ? numberFormat(
                            parseFloat(caseDetails.totalClaimValue).toFixed(2),
                            caseDetails?.currencyUnit,
                          )
                        : "Non Monetary"}
                    </span>

                    {viewOnly !== true &&
                      caseDetails?.status !== "draft" &&
                      caseDetails?.status !== "pendingRegistrationFee" &&
                      caseDetails?.status !== "proformaGenerated" &&
                      caseDetails?.status !== "waitingForCaseApproval" && (
                        <UpdateLink
                          style={{ marginTop: 3 }}
                          onClick={() => setClaimValueModal(true)}
                        >
                          Edit
                        </UpdateLink>
                      )}
                  </ClaimValueRow>

                  {caseDetails?.resolutionKind === "mediation" &&
                  caseDetails?.caseItems?.length ? (
                    <MorePartiesLink
                      onClick={() =>
                        setOpenDrawerClaim((a) => ({ ...a, state: true }))
                      }
                    >
                      + View Items
                    </MorePartiesLink>
                  ) : null}

                  {caseDetails?.resolutionKind === "arbitration" &&
                  caseDetails?.counterClaimValue !== 0 ? (
                    <MorePartiesLink onClick={() => setClaimModal(true)}>
                      + Counter Claim
                    </MorePartiesLink>
                  ) : null}
                </InfoValue>
              </InfoCard>

              <InfoCard>
                <InfoLabel>Filed on</InfoLabel>
                <InfoValue>
                  {caseDetails.created_at &&
                    moment(parseTimeStamp(caseDetails.created_at)).format(
                      "DD/MM/YYYY",
                    )}
                </InfoValue>
              </InfoCard>

              <InfoCard>
                <InfoLabel>Last Updated</InfoLabel>
                <InfoValue>
                  {caseDetails.updated_at &&
                    moment(parseTimeStamp(caseDetails.updated_at)).format(
                      "DD/MM/YYYY",
                    )}
                </InfoValue>
              </InfoCard>

              {caseDetails?.status !== "draft" &&
                caseDetails?.status !== "draft" &&
                caseDetails?.status !== "pendingRegistrationFee" &&
                caseDetails?.status !== "proformaGenerated" &&
                caseDetails?.status !== "waitingForCaseApproval" && (
                  <InfoCard>
                    <InfoLabel>Next Hearing</InfoLabel>
                    <HearingInfo
                      onClick={() =>
                        caseDetails?.hearingDatesSet?.length > 0 &&
                        setTimelineModal({
                          state: true,
                          data: caseDetails?.hearingDatesSet,
                        })
                      }
                      clickable={caseDetails?.hearingDatesSet?.length > 0}
                    >
                      {caseDetails?.nextHearingDate?.date?.length
                        ? caseDetails?.nextHearingDate?.time
                        : "N/A"}
                    </HearingInfo>
                    {viewOnly !== true && (
                      <UpdateLink
                        onClick={() => {
                          setOpenHearing((a) => ({ ...a, state: true }));
                          setTimelineModal({ state: false });
                        }}
                      >
                        Update
                      </UpdateLink>
                    )}
                  </InfoCard>
                )}
            </GridRow>

            <ActionButtonsContainer>
              <ActionGroup>
                <HyperLink onClick={() => setState(true)}>
                  Case Documents
                </HyperLink>

                {partyList.length > 0 && (
                  <HyperLink
                    onClick={() =>
                      setOpenDrawer((a) => ({ ...a, state: true }))
                    }
                  >
                    Agent Details
                  </HyperLink>
                )}

                <HyperLink
                  onClick={() => !disableRefund && setOpenRefundModal(true)}
                  disabled={disableRefund}
                >
                  Refund
                </HyperLink>

                {caseDetails?.resolutionKind !== "negotiation" && (
                  <HyperLink onClick={() => setViewNotes(true)}>
                    Scratch Pad
                  </HyperLink>
                )}

                {caseDetails?.resolutionKind === "arbitration" && (
                  <HyperLink onClick={() => setFields(true)}>
                    Case Details
                  </HyperLink>
                )}

                {viewOnly !== true && (
                  <HyperLink
                    onClick={() =>
                      setInvoiceModal({ state: true, caseId: caseDetails.id })
                    }
                  >
                    Generate Invoice
                  </HyperLink>
                )}
                {caseDetails?.trackingNumbers?.length > 0 && (
                  <HyperLink onClick={openTrackingModal}>
                    Tracking Covers
                  </HyperLink>
                )}
                {caseDetails?.resolutionKind === "mediation" &&
                  caseDetails?.smartODRDisputeId && (
                    <HyperLink onClick={() => fetchReportDetails()}>
                      Smart ODR
                    </HyperLink>
                  )}
                {caseDetails?.resolutionKind === "mediation" &&
                  caseDetails?.smartODRDisputeId && (
                    <HyperLink onClick={() => postReportDetails()}>
                      Trigger Smart ODR
                    </HyperLink>
                  )}
              </ActionGroup>
            </ActionButtonsContainer>
          </InfoGridContainer>

          <DescriptionSection>
            <Accordion className={classes.root}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <DescriptionHeading>
                  Case Description
                  {caseDetails?.caseTopic?.length > 0 && (
                    <KeywordsTag>
                      Keywords: {caseDetails.caseTopic.join(", ")}
                    </KeywordsTag>
                  )}
                </DescriptionHeading>
              </AccordionSummary>
              <DescriptionContent>
                {caseDetails?.description}
              </DescriptionContent>
            </Accordion>
          </DescriptionSection>

          {caseDetails?.resolutionKind === "negotiation" && (
            <FooterInfo>
              <FooterItem>
                <FooterLabel>Total Rounds:</FooterLabel>
                <FooterValue>{caseDetails?.negotiationRoundLimit}</FooterValue>
              </FooterItem>
            </FooterInfo>
          )}
        </ExpansionPanelDetails>
      </ExpansionPanel>

      <ClaimValueModal
        open={claimValueModal}
        onClose={() => setClaimValueModal(false)}
        caseDetails={caseDetails}
        onSuccess={() => {
          if (MTRef?.current?.onSearchChange) MTRef.current.onSearchChange("");
          setRefreshDetails((r) => !r);
        }}
      />

      {respondentModal && (
        <RespondentPartiesModal
          open={respondentModal}
          setOpen={setRespondentModal}
          respondentParties={caseDetails?.respondentParties}
          id={caseDetails.id}
        />
      )}
      {trackingModal?.state && (
        <TrackingModal {...{ trackingModal, setTrackingModal }} />
      )}
      {invoiceModal?.state && (
        <GenerateInvoiceModal
          {...{ invoiceModal, setInvoiceModal }}
          caseId={caseDetails.id}
        />
      )}
      {disputeModal?.state && (
        <DisputeModal
          {...{
            disputeModal,
            setDisputeModal,
            caseDetails,
            onContinue: onSmartOdrActionContinue,
            onTrigger: postReportDetails,
          }}
        />
      )}
      <AgentListModal
        {...{
          opendrawer,
          setOpenDrawer,
          MTRef,
          partyList,
          displayAuthUrl: false,
          caseDetails,
          setRefreshDetails,
          viewOnly,
        }}
      />
      <ClaimItemsListModal
        {...{
          opendrawerClaim,
          setOpenDrawerClaim,
          MTRef,
          displayAuthUrl: false,
        }}
        caseDetails={caseDetails}
      />
      <CounterClaimModal
        {...{ claimModal, setClaimModal, MTRef, caseDetails }}
      />
      <HearingDate
        {...{ openHearing, setOpenHearing, refreshDetails, setRefreshDetails }}
        caseDetails={caseDetails}
      />
      <ViewNotesModal
        disabled={isActionsDisabled}
        modal={viewNotes}
        setModal={setViewNotes}
        type={"Case Manager"}
        id={id}
        {...{ viewOnly }}
      />
      <CaseDescriptionModal
        {...{ modal, setModal }}
        description={caseDetails?.description}
      />
      <RefundModal
        id={caseDetails?.id}
        {...{ openRefundModal, setOpenRefundModal, viewOnly }}
        heading="Refund Payment"
        btnText="Refund Payment"
      />
      {timelineModal?.state && (
        <HearingTimeline {...{ timelineModal, setTimelineModal }} />
      )}
      <DocumentUploadModal
        modal={docUploadModal}
        setModal={setDocUploadModal}
        handleUpload={s3Upload}
        file={file}
        fileRef={fileRef}
        url={url}
        data={data}
        setData={setData}
        setUrl={setUrl}
        resolutionKind={caseDetails?.resolutionKind}
        shareDefault={caseDetails?.resolutionKind}
        handleSuccess={(data) => {
          setOpenAlert(true);
          setAlertData({
            clickSecondarybtn: () => setOpenAlert(false),
            onDialogClose: () => setOpenAlert(false),
            primaryBtnText: "OK",
            secondaryBtnText: "Cancel",
            desc:
              data && data.length === 1
                ? `${
                    data[0]["visibilityType"] === "all"
                      ? "Are you sure you want to share this document with all parties and Neutral (s)?"
                      : data[0]["visibilityType"] === "me"
                        ? "Are you sure you want to keep this document private?"
                        : "Are you sure you want to share this document with the Neutral(s)?"
                  }`
                : "Are you sure you want to share this document?",
            heading: `Alert`,
            clickPrimaryBtn: () => addDocumentForCase(data),
          });
        }}
        hasDocumentType={caseDetails?.resolutionKind === "arbitration"}
      />
      <AlertDialog
        isOpen={openAlert}
        {...{ ...alertData }}
        descriptionTextStyle={{ textAlign: "left" }}
      />
    </>
  );
};

export default Index;

export const fadeIn = keyframes`from { opacity: 0 } to { opacity: 1 }`;
export const slideUp = keyframes`
 from { opacity: 0; transform: translateY(20px) scale(0.98) }
 to { opacity: 1; transform: translateY(0) scale(1) }
`;
const spin = keyframes`from { transform: rotate(0deg) } to { transform: rotate(360deg) }`;

const HeaderWrapper = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 24px;
  width: 100%;
  gap: 16px;
`;

const CaseTitleSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

const CaseTitle = styled(Heading)`
  margin: 0 !important;
  font-size: 18px !important;
`;

const RightActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
`;

const ToggleButton = styled.button`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 600;
  color: #293461;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  &:hover {
    background: #f7fafc;
  }
`;

const InfoGridContainer = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const GridRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
`;

const InfoCard = styled.div`
  background: #f8f9fa;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  transition: all 0.3s ease;
  &:hover {
    border-color: #cbd5e0;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  }
`;

const InfoLabel = styled.label`
  display: block;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  color: #949ab0;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
`;

const InfoValue = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #293461;
  word-break: break-word;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ClaimValueRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const MorePartiesLink = styled.button`
  background: none;
  border: none;
  color: #00838c;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  padding: 0;
  margin-top: 4px;
  transition: all 0.2s ease;
  &:hover {
    text-decoration: underline;
  }
`;

const HearingInfo = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #293461;
  cursor: ${(p) => (p.clickable ? "pointer" : "default")};
  display: flex;
  flex-direction: column;
  gap: 6px;
  transition: all 0.2s ease;
  &:hover {
    color: ${(p) => (p.clickable ? "#00838c" : "#1a202c")};
  }
`;

const UpdateLink = styled.button`
  background: none;
  border: none;
  color: #00838c;
  cursor: pointer;
  font-size: 11px;
  font-weight: 600;
  padding: 0;
  transition: all 0.2s ease;
  align-self: flex-start;
  &:hover {
    text-decoration: underline;
  }
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
`;

const ActionGroup = styled.div`
  display: flex;
  flex-direction: row-reverse;
  gap: 18px;
  align-items: center;
  flex-wrap: nowrap;
`;

// ── Description ──
const DescriptionSection = styled.div`
  padding: 0 24px 24px;
`;

const DescriptionHeading = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  margin-left: 8px;
  font-weight: 600;
  color: #293461;
`;

const KeywordsTag = styled.span`
  background: #fef3c7;
  color: #92400e;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  margin-left: 8px;
`;

const DescriptionContent = styled.div`
  font-size: 14px;
  word-break: break-word;
  text-align: justify;
  color: #293461;
  font-family: ${theme.fonts.primaryFontSemiBold};
  padding: 16px;
  background: white;
  border-radius: 6px;
  border-left: 4px solid #00838c;
  max-height: 400px;
  overflow-y: auto;
  & strong,
  & bold {
    font-family: ${theme.fonts.primaryFontExtraBold};
    color: #1a202c;
  }
  & .ql-align-center {
    text-align: center;
  }
  & .ql-align-right {
    text-align: right;
  }
`;

const FooterInfo = styled.div`
  padding: 16px 24px;
  background: #f0f4f8;
  border-top: 1px solid #e2e8f0;
  border-radius: 0 0 8px 8px;
`;

const FooterItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FooterLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  color: #718096;
  letter-spacing: 0.5px;
`;

const FooterValue = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: #1a202c;
`;

export const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1300;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  animation: ${fadeIn} 0.2s ease;
`;

export const ModalBox = styled.div`
  background: #ffffff;
  border-radius: 16px;
  width: 100%;
  max-width: 460px;
  box-shadow:
    0 24px 64px rgba(41, 52, 97, 0.16),
    0 4px 16px rgba(0, 0, 0, 0.08),
    0 0 0 1px rgba(226, 232, 240, 0.8);
  overflow: hidden;
  animation: ${slideUp} 0.28s cubic-bezier(0.34, 1.3, 0.64, 1);
`;

export const ModalHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 22px 24px 18px;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-bottom: 1px solid #e2e8f0;
`;

export const ModalTitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

export const ModalTitle = styled.h2`
  font-size: 16px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: #293461;
  margin: 0 0 2px;
  letter-spacing: -0.2px;
`;

export const ModalSub = styled.p`
  font-size: 12px;
  color: #949ab0;
  margin: 0;
  font-weight: 400;
`;

export const ModalBody = styled.div`
  padding: 22px 24px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const FieldLabel = styled.label`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #949ab0;
  display: flex;
  align-items: center;
  gap: 3px;
`;

export const CurrencyHint = styled.p`
  font-size: 11.5px;
  color: #949ab0;
  margin: 0;
  font-weight: 500;
`;

export const ModalFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  background: #f8f9fa;
`;

export const SaveBtn = styled.button`
  padding: 10px 22px;
  background: #00838c;
  color: #ffffff;
  border: none;
  border-radius: 9px;
  font-size: 13.5px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 150px;
  justify-content: center;
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const SpinnerWrap = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Spinner = styled.span`
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
  display: inline-block;
`;
